import clsx from 'clsx';
import { formatDateSafe } from '@/utils/dateUtils';
import {
    BALANCE_CARD_LABELS,
    DEADLINE_IDLE_VALUES,
    INDICATOR_TONE_CLASSES,
    INDICATOR_VALUE_CLASSES,
} from './vacationBalanceCards.definitions';
import type {
    IndicatorProps,
    VacationBalanceCardsProps,
} from './vacationBalanceCards.types';

function Indicator({ label, value, subValue, tone = 'neutral', valueSize = 'number' }: IndicatorProps) {
    return (
        <div className={clsx('h-full rounded-xl p-4', INDICATOR_TONE_CLASSES[tone])}>
            <p className="text-sm font-medium text-raro-gray-500 font-montserrat">{label}</p>
            <p
                className={clsx(
                    'mt-1 font-semibold font-montserrat',
                    valueSize === 'text' ? 'text-xl' : 'text-3xl',
                    INDICATOR_VALUE_CLASSES[tone]
                )}
            >
                {value}
            </p>
            {subValue && (
                <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">{subValue}</p>
            )}
        </div>
    );
}

export function VacationBalanceCards({
    balanceDays,
    expiredDays,
    scheduledDays,
    deadline,
    deadlineVariant = 'deadline',
    nextEntitlementDate,
}: VacationBalanceCardsProps) {
    const hasExpired = expiredDays > 0;
    const isEntitledFrom = deadlineVariant === 'entitledFrom';
    const isIdleVariant = deadlineVariant === 'upToDate' || deadlineVariant === 'allScheduled';

    const deadlineLabel = isIdleVariant
        ? BALANCE_CARD_LABELS.status
        : isEntitledFrom
            ? BALANCE_CARD_LABELS.entitledFrom
            : BALANCE_CARD_LABELS.nextDeadline;

    const deadlineValue = isIdleVariant
        ? DEADLINE_IDLE_VALUES[deadlineVariant]
        : deadline
            ? formatDateSafe(deadline)
            : '—';

    const deadlineSubValue = isIdleVariant && nextEntitlementDate
        ? `Próximo direito em ${formatDateSafe(nextEntitlementDate)}`
        : undefined;

    const deadlineTone = isIdleVariant
        ? deadlineVariant === 'upToDate'
            ? 'success'
            : 'primary'
        : 'primary';

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-tour="vacation-balance">
            <div>
                <Indicator
                    label={BALANCE_CARD_LABELS.balance}
                    value={`${balanceDays} dias`}
                    tone="primary"
                />
            </div>
            <div data-tour="vacation-expired">
                <Indicator
                    label={BALANCE_CARD_LABELS.expired}
                    value={`${expiredDays} dias`}
                    subValue={hasExpired ? 'Venceram em definitivo' : 'Nenhum período vencido'}
                    tone={hasExpired ? 'danger' : 'primary'}
                />
            </div>
            <div data-tour="vacation-deadline">
                <Indicator
                    label={deadlineLabel}
                    value={deadlineValue}
                    subValue={deadlineSubValue}
                    tone={deadlineTone}
                    valueSize={isIdleVariant ? 'text' : 'number'}
                />
            </div>
            <div data-tour="vacation-scheduled">
                <Indicator
                    label={BALANCE_CARD_LABELS.scheduled}
                    value={`${scheduledDays} dias`}
                    tone="primary"
                />
            </div>
        </div>
    );
}

export default VacationBalanceCards;
